import type { PreferencesSection } from 'src/blocks/preferences-dialog/types';

import { SECTION_IDS } from './constants';
import type {
  DetailsSectionData,
  RatingSectionData,
  StoredPreferences,
} from './types';

interface PreferenceEntry {
  id: string;
  enabled: boolean;
}

const mergeByPreferences = <TSection extends { id: string }, TResult>(
  sections: TSection[],
  preferences: PreferenceEntry[],
  map: (section: TSection, enabled: boolean) => TResult | null
): TResult[] => {
  const prefIds = new Set(preferences.map((p) => p.id));
  const sectionById = new Map(sections.map((s) => [s.id, s]));
  const result: TResult[] = [];

  for (const pref of preferences) {
    const section = sectionById.get(pref.id);
    if (section) {
      const mapped = map(section, pref.enabled);
      if (mapped !== null) {
        result.push(mapped);
      }
    }
  }

  for (const section of sections) {
    if (!prefIds.has(section.id)) {
      const mapped = map(section, true);
      if (mapped !== null) {
        result.push(mapped);
      }
    }
  }

  return result;
};

// Sections missing from preferences are appended at the end so newly
// introduced sections show by default.
export const applyPreferences = (
  sections: RatingSectionData[],
  preferences: StoredPreferences
): RatingSectionData[] =>
  mergeByPreferences(sections, preferences.sections, (section, enabled) =>
    enabled ? section : null
  );

// Prepends a locked Details row so the user sees Details listed but cannot
// toggle or reorder it.
export const buildPreferencesSections = (
  details: DetailsSectionData,
  sections: RatingSectionData[],
  preferences: StoredPreferences
): PreferencesSection[] => [
  {
    id: SECTION_IDS.details,
    label: details.title,
    enabled: true,
    locked: true,
  },
  ...mergeByPreferences(sections, preferences.sections, (section, enabled) => ({
    id: section.id,
    label: section.title,
    enabled,
  })),
];

export const toStoredPreferences = (
  sections: PreferencesSection[]
): StoredPreferences => ({
  sections: sections
    .filter((s) => !s.locked)
    .map(({ id, enabled }) => ({ id, enabled })),
});
